import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useNespresso } from "../context/NespressoContext";

// Nespresso brand colors
const NESPRESSO = {
  brown: "#986f38",
  brownHover: "#815e2f",
  gold: "#c69f36",
  goldLight: "#d4af37",
  black: "#000000",
  darkGray: "#292929",
  lightGray: "#f9f9f9",
  border: "#e6e5e4",
};

// Demo user name - in production would come from auth
const DEMO_USER_NAME = "Jamie";

const NespressoConfirmationView: React.FC = () => {
  const navigate = useNavigate();
  const { state, dispatch, currentTier, nextTier, podsToNext } = useNespresso();
  const [sessionId] = useState(() => `nespresso-${Date.now()}`);
  const [showConfetti, setShowConfetti] = useState(true);
  const [displayedPoints, setDisplayedPoints] = useState(0);

  // Points = pods (1 point per pod per spec)
  const totalPoints = state.gamification.lifetimePods;
  const totalCapsules = state.gamification.lifetimePods;

  // Animated points counter
  useEffect(() => {
    if (displayedPoints < totalPoints) {
      const timer = setTimeout(() => {
        const increment = Math.max(1, Math.ceil((totalPoints - displayedPoints) / 15));
        setDisplayedPoints((prev) => Math.min(totalPoints, prev + increment));
      }, 40);
      return () => clearTimeout(timer);
    }
  }, [displayedPoints, totalPoints]);

  // Generate encouragement message based on tier per spec
  const getEncouragementMessage = () => {
    if (currentTier.id === "grand-cru") {
      return {
        title: `${DEMO_USER_NAME}, you recycling legend!`,
        subtitle: "Grand Cru status unlocked - you've reached the pinnacle of purpose!",
      };
    }

    if (nextTier) {
      const nearMilestone = podsToNext <= 50;
      if (nearMilestone) {
        return {
          title: `${DEMO_USER_NAME}, you're so close!`,
          subtitle: `Just ${podsToNext} more capsules to unlock ${nextTier.name}!`,
        };
      }

      return {
        title: `${DEMO_USER_NAME}, you recycling champion!`,
        subtitle: `${nextTier.name} is on the horizon - ${podsToNext} capsules to go`,
      };
    }

    return {
      title: `${DEMO_USER_NAME}, you recycling star!`,
      subtitle: "Keep recycling to climb the tiers!",
    };
  };

  const encouragement = getEncouragementMessage();

  // Celebration effect and send completion event on mount
  useEffect(() => {
    // Hide confetti after celebration duration
    const confettiTimer = setTimeout(() => setShowConfetti(false), 4000);

    // Send completion event to PIC API
    const sendCompletionEvent = async () => {
      try {
        await fetch(
          "https://agentic-commerce.pentatonic.com/api/v1/pin/events",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Tenant-ID": "nespresso",
            },
            body: JSON.stringify({
              sessionId,
              eventType: "recycling_completed",
              eventData: {
                pointsEarned: totalPoints,
                capsuleCount: totalCapsules,
                store: "Nespresso Boutique",
              },
              timestamp: new Date().toISOString(),
            }),
          }
        );
      } catch (error) {
        console.log("Event tracking skipped (demo mode)");
      }
    };

    sendCompletionEvent();

    // Store session ID for potential future reference
    localStorage.setItem("nespresso_last_session", sessionId);

    return () => clearTimeout(confettiTimer);
  }, [sessionId, totalPoints, totalCapsules]);

  const handleDone = () => {
    dispatch({ type: "RESET_GAMIFICATION" });
    navigate("/nespresso");
  };

  const handleNewRecycling = () => {
    navigate("/nespresso/takeback");
  };

  // Share achievement (Step 5)
  const handleShare = () => {
    navigate("/nespresso/share");
  };

  // View offers (Step 6)
  const handleViewOffers = () => {
    navigate("/nespresso/offer");
  };

  const steps = [
    {
      title: "Sort & Separate",
      description: "Aluminum separated from coffee grounds",
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
          />
        </svg>
      ),
    },
    {
      title: "Recycle Aluminum",
      description: "Capsules melted for infinite reuse",
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
          />
        </svg>
      ),
    },
    {
      title: "Compost Grounds",
      description: "Coffee becomes nutrient-rich soil",
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
    },
  ];

  return (
    <div
      className="min-h-screen flex flex-col relative overflow-hidden"
      style={{ backgroundColor: NESPRESSO.black }}
    >
      {/* Gold Confetti Celebration */}
      <AnimatePresence>
        {showConfetti && (
          <div className="absolute inset-0 pointer-events-none z-10">
            {[...Array(40)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute rounded-full"
                style={{
                  width: `${4 + Math.random() * 8}px`,
                  height: `${4 + Math.random() * 8}px`,
                  backgroundColor:
                    i % 4 === 0
                      ? NESPRESSO.gold
                      : i % 4 === 1
                      ? NESPRESSO.goldLight
                      : i % 4 === 2
                      ? NESPRESSO.brown
                      : "#ffffff",
                  left: `${Math.random() * 100}%`,
                }}
                initial={{ y: -20, opacity: 1, rotate: 0 }}
                animate={{
                  y: "100vh",
                  opacity: [1, 1, 0],
                  rotate: Math.random() * 720 - 360,
                }}
                exit={{ opacity: 0 }}
                transition={{
                  duration: 3 + Math.random() * 2,
                  delay: Math.random() * 0.8,
                  ease: "easeOut",
                }}
              />
            ))}
          </div>
        )}
      </AnimatePresence>

      {/* Header */}
      <header className="py-6 px-8 flex items-center justify-center relative z-20">
        <img
          src="/assets/images/logos/nespresso-white.png"
          alt="Nespresso"
          className="h-6 w-auto"
        />
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-center px-8 py-8 relative z-20">
        {/* Success Icon with Gold Glow */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", duration: 0.6 }}
          className="relative mb-6"
        >
          {/* Pulsing gold glow effect */}
          <motion.div
            className="absolute inset-0 rounded-full blur-2xl"
            style={{ backgroundColor: NESPRESSO.gold }}
            animate={{
              opacity: [0.3, 0.6, 0.3],
              scale: [1.2, 1.5, 1.2],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
          {/* Success circle */}
          <motion.div
            className="relative w-24 h-24 rounded-full flex items-center justify-center"
            style={{
              background: `linear-gradient(135deg, ${NESPRESSO.gold} 0%, ${NESPRESSO.goldLight} 50%, ${NESPRESSO.gold} 100%)`,
              boxShadow: `0 10px 40px ${NESPRESSO.gold}66`,
            }}
            animate={{
              boxShadow: [
                `0 10px 40px ${NESPRESSO.gold}66`,
                `0 15px 50px ${NESPRESSO.gold}88`,
                `0 10px 40px ${NESPRESSO.gold}66`,
              ],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          >
            <svg
              className="w-12 h-12 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <motion.path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={3}
                d="M5 13l4 4L19 7"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 0.6, delay: 0.3 }}
              />
            </svg>
          </motion.div>
        </motion.div>

        {/* Encouragement Message (per spec) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="text-center mb-6"
        >
          <h1 className="text-4xl font-light text-white mb-2">
            {encouragement.title}
          </h1>
          <p className="text-lg text-white/60 font-light">
            {encouragement.subtitle}
          </p>
        </motion.div>

        {/* Account Updated Message + Animated Points */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="text-center mb-8"
        >
          <p className="text-white/50 text-sm mb-2">Your account has been updated</p>
          <motion.div
            className="text-6xl font-light"
            style={{ color: NESPRESSO.gold }}
            key={displayedPoints}
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
          >
            +{displayedPoints}
          </motion.div>
          <p className="text-white/60 mt-1">points earned</p>
        </motion.div>

        {/* Lifetime Summary Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="relative bg-white/5 border border-white/10 rounded-lg p-6 max-w-md w-full mb-8"
        >
          <h3 className="text-white font-medium text-center mb-4">Lifetime Summary</h3>

          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="text-center">
              <p className="text-white/50 text-xs mb-1">Total Capsules</p>
              <p className="text-xl font-medium text-white">{totalCapsules}</p>
            </div>
            <div className="text-center">
              <p className="text-white/50 text-xs mb-1">Current Tier</p>
              <div className="flex items-center justify-center gap-1">
                <div
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: currentTier.badge.color }}
                />
                <p className="text-xl font-medium" style={{ color: NESPRESSO.gold }}>
                  {currentTier.name}
                </p>
              </div>
            </div>
            <div className="text-center">
              <p className="text-white/50 text-xs mb-1">Aluminum Saved</p>
              <p className="text-xl font-medium text-white">
                {(totalCapsules * 0.8).toFixed(0)}g
              </p>
            </div>
          </div>

          {/* Progress to next tier */}
          {nextTier && (
            <div className="pt-4 border-t border-white/10">
              <div className="flex items-center justify-between mb-2 text-sm">
                <span className="text-white/50">{currentTier.name}</span>
                <span className="text-white/50">{nextTier.name}</span>
              </div>
              <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
                <motion.div
                  className="h-full"
                  style={{ backgroundColor: NESPRESSO.gold }}
                  initial={{ width: 0 }}
                  animate={{ width: `${((currentTier.threshold > 0 ? totalCapsules - currentTier.threshold : totalCapsules) / (nextTier.threshold - currentTier.threshold)) * 100}%` }}
                  transition={{ duration: 1, delay: 0.5 }}
                />
              </div>
              <p className="text-white/40 text-xs text-center mt-2">
                {podsToNext} capsules to {nextTier.name}
              </p>
            </div>
          )}
        </motion.div>

        {/* What Happens Next */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="max-w-2xl w-full mb-8"
        >
          <h3 className="text-white font-medium text-center mb-6">
            What Happens to Your Capsules
          </h3>
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <React.Fragment key={step.title}>
                <motion.div
                  className="flex flex-col items-center text-center"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 + index * 0.1 }}
                >
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center mb-3"
                    style={{
                      backgroundColor: `${NESPRESSO.gold}15`,
                      color: NESPRESSO.gold,
                      border: `1px solid ${NESPRESSO.gold}30`,
                    }}
                  >
                    {step.icon}
                  </div>
                  <h4 className="text-white font-medium text-sm mb-1">
                    {step.title}
                  </h4>
                  <p className="text-white/50 text-xs max-w-[120px] font-light">
                    {step.description}
                  </p>
                </motion.div>
                {index < steps.length - 1 && (
                  <motion.div
                    className="flex-1 h-px mx-4 mt-[-24px]"
                    style={{
                      background: `linear-gradient(90deg, ${NESPRESSO.gold}40, ${NESPRESSO.gold}20, ${NESPRESSO.gold}40)`,
                    }}
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ delay: 0.6 + index * 0.1, duration: 0.3 }}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </motion.div>

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="flex flex-col items-center gap-4"
        >
          {/* Primary actions row */}
          <div className="flex gap-4">
            {/* Share button - Step 5 */}
            <motion.button
              onClick={handleShare}
              className="px-8 py-3 text-white font-medium transition-all flex items-center gap-2"
              style={{
                borderRadius: "3px",
                backgroundColor: `${NESPRESSO.gold}20`,
                border: `1px solid ${NESPRESSO.gold}40`,
              }}
              whileHover={{
                backgroundColor: `${NESPRESSO.gold}30`,
                borderColor: `${NESPRESSO.gold}60`,
              }}
              whileTap={{ scale: 0.98 }}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
              </svg>
              Share Achievement
            </motion.button>

            {/* View Offers button - Step 6 */}
            <motion.button
              onClick={handleViewOffers}
              className="px-8 py-3 font-medium transition-colors"
              style={{
                background: `linear-gradient(135deg, ${NESPRESSO.gold} 0%, ${NESPRESSO.goldLight} 100%)`,
                color: NESPRESSO.black,
                borderRadius: "3px",
                boxShadow: `0 4px 20px ${NESPRESSO.gold}40`,
              }}
              whileHover={{
                boxShadow: `0 6px 25px ${NESPRESSO.gold}60`,
                scale: 1.02,
              }}
              whileTap={{ scale: 0.98 }}
            >
              View Offers
            </motion.button>
          </div>

          {/* Secondary actions row */}
          <div className="flex gap-4">
            <button
              onClick={handleNewRecycling}
              className="px-6 py-2 text-white/60 hover:text-white text-sm font-medium transition-colors"
            >
              Recycle More
            </button>
            <button
              onClick={handleDone}
              className="px-6 py-2 text-white/60 hover:text-white text-sm font-medium transition-colors"
            >
              Done
            </button>
          </div>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="py-4 px-8 text-center text-white/40 text-sm border-t border-white/10 font-light">
        <p>Your credit has been added to your Nespresso Club account</p>
      </footer>
    </div>
  );
};

export default NespressoConfirmationView;
